import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert, CircularProgress, Grid, Typography
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Edit as EditIcon } from '@mui/icons-material';
import { getBranches, createBranch, updateBranch, deleteBranch } from '../../services/api';
import { isAdmin } from '../../utils/auth';

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ branch_code: '', name: '', address: '', city: '', phone: '', email: '', manager_name: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { setLoading(true); const r = await getBranches(); setBranches(r.data || []); }
    catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditItem(null); setForm({ branch_code: '', name: '', address: '', city: '', phone: '', email: '', manager_name: '' }); setDialogOpen(true); };
  const openEdit = (b) => { setEditItem(b); setForm({ ...b }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editItem) await updateBranch(editItem.id, form);
      else await createBranch(form);
      setDialogOpen(false);
      loadData();
    } catch (err) { setError(err.message); }
  };

  if (!isAdmin()) {
    return <Alert severity="warning">Only super admin or admin can manage branches.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>Branch Management</Typography>
          <Typography color="text.secondary">Create and manage company branches</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Create Branch</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>City</TableCell><TableCell>Phone</TableCell><TableCell>Manager</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {branches.map(b => (
                <TableRow key={b.id}>
                  <TableCell><strong>{b.branch_code}</strong></TableCell>
                  <TableCell>{b.name}</TableCell>
                  <TableCell>{b.city}</TableCell>
                  <TableCell>{b.phone}</TableCell>
                  <TableCell>{b.manager_name}</TableCell>
                  <TableCell><Chip label={b.is_active ? 'Active' : 'Inactive'} size="small" color={b.is_active ? 'success' : 'default'} /></TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(b)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => deleteBranch(b.id).then(loadData)}>Deactivate</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Branch' : 'Create Branch'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}><TextField fullWidth label="Branch Code" value={form.branch_code} onChange={(e) => setForm({ ...form, branch_code: e.target.value.toUpperCase() })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Branch Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Manager Name" value={form.manager_name} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSave}>Save</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default Branches;
